<template>
  <div class="home-classic">
    <b-alert variant="success" v-model="localShowSigninAlert" dismissible>
      You have been signed in for the Public Water System {{ pws_id }} in the
      role {{ role }}.
    </b-alert>
    <b-alert variant="danger" v-model="localShowAuthError" dismissible>
      {{ local_auth_error_message }}
    </b-alert>

    <!-- Bacteriological sampling announcement (dismissible: announcements.js) -->
    <b-card v-if="bactShown" class="mb-4 announcement-card" border-variant="warning" bg-variant="light">
      <b-row align-v="center" class="clickable" @click="showBactFormModal = true">
        <b-col cols="auto">
          <b-icon icon="exclamation-triangle-fill" font-scale="2.5" variant="warning"></b-icon>
        </b-col>
        <b-col>
          <h5 class="mb-1 text-warning">Important messages regarding bacteriological sampling</h5>
          <p class="mb-0">
            Click here for information about the revised Form 425 and Bact bar code label instructions.
          </p>
        </b-col>
        <b-col cols="auto">
          <b-button variant="warning" @click.stop="showBactFormModal = true">
            Read More
          </b-button>
        </b-col>
        <b-col cols="auto" class="pl-0">
          <b-button variant="link" class="announcement-dismiss" title="Dismiss"
                    aria-label="Dismiss" @click.stop="dismissBact">
            <b-icon icon="x" font-scale="1.6"></b-icon>
          </b-button>
        </b-col>
      </b-row>
    </b-card>


    <b-container fluid class="p-0">
      <!-- Needs Attention Section -->
      <div v-if="signed_in && (sigdef_status.needs_attention || message_count > 0 || lsli_notice_due || vocWaiverActionNeeded || rtcrCorrections || rtcrPlanDue)" class="mb-4">
        <h3>Needs Your Attention</h3>
        <b-row>
          <b-col md v-if="sigdef_status.needs_attention">
            <b-button class="my-1" block size="lg" variant="danger" to="sigdef-responses">
              <b-icon icon="clipboard-check" />
              Significant Deficiencies
              <b-badge variant="light">!</b-badge>
            </b-button>
          </b-col>
          <b-col md v-if="message_count > 0">
            <b-button class="my-1" block size="lg" variant="warning" to="messages">
              <b-icon icon="inbox-fill" />
              Messages
              <b-badge variant="light">{{ message_count }}</b-badge>
            </b-button>
          </b-col>
          <b-col md v-if="lsli_notice_due">
            <b-button class="my-1" block size="lg" variant="danger" to="lead-inventory">
              <b-icon icon="droplet-half" />
              Lead Service Line Notice
              <b-badge variant="light">{{ lsli_notice_year }} Due</b-badge>
            </b-button>
          </b-col>
          <b-col md v-if="rtcrCorrections || rtcrPlanDue">
            <b-button class="my-1 rtcr-attention" block size="lg" variant="warning" to="rtcr">
              <b-icon icon="map" />
              RTCR Sample Site Plan
              <b-badge variant="light">{{ rtcrCorrections ? 'Corrections Required' : rtcrPlanDue }}</b-badge>
            </b-button>
          </b-col>
          <b-col md v-if="vocWaiverActionNeeded">
            <b-button class="my-1" block size="lg" variant="info" to="monitoring-waivers">
              <b-icon icon="clipboard-check" />
              VOC Monitoring Waiver
              <b-badge variant="light">Apply</b-badge>
            </b-button>
          </b-col>
        </b-row>
      </div>

      <h3>Applications</h3>

      <!-- Reports & Submissions -->
      <h5 class="section-heading">Reports &amp; Submissions</h5>
      <b-row class="mt-3">
        <b-col md>
          <b-button v-if="!signed_in" v-b-modal.sign-in-modal class="my-1" block size="lg" variant="primary">
            <b-icon icon="card-list" />
            Public Water System Report
          </b-button>
          <b-button v-else class="my-1" :class="{ 'has-due': annualReportDue }" block size="lg" variant="primary" to="pws-report-status">
            <b-icon icon="card-list" />
            Public Water System Report
            <small v-if="annualReportDue" class="due-note">{{ annualReportDue }}</small>
          </b-button>
        </b-col>
        <b-col md>
          <b-button class="my-1" block size="lg" variant="primary" to="ccr">
            <b-icon icon="newspaper" />
            Consumer Confidence Report
          </b-button>
        </b-col>
      </b-row>
      <b-row>
        <b-col md>
          <b-button v-if="!signed_in" v-b-modal.sign-in-modal class="my-1" block size="lg" variant="primary">
            <b-icon icon="journal-check" />
            Surveys
          </b-button>
          <b-button v-else class="my-1" block size="lg" variant="primary" to="surveys">
            <b-icon icon="journal-check" />
            Surveys
          </b-button>
        </b-col>
        <b-col md>
          <!-- Empty for alignment -->
        </b-col>
      </b-row>

      <!-- Water Quality Testing -->
      <h5 class="section-heading mt-4">Water Quality Testing</h5>
      <b-row>
        <b-col md>
          <b-button v-if="!signed_in" v-b-modal.sign-in-modal class="my-1" block size="lg" variant="primary">
            <b-icon icon="droplet" />
            Sample Results
          </b-button>
          <b-button v-else class="my-1" block size="lg" variant="primary" to="sample-results">
            <b-icon icon="droplet" />
            Sample Results
          </b-button>
        </b-col>
        <b-col md>
          <b-button v-if="!signed_in" v-b-modal.sign-in-modal class="my-1" block size="lg" variant="primary">
            Sample Schedules
          </b-button>
          <b-button v-else class="my-1" block size="lg" variant="primary" to="sample-schedules">
            Sample Schedules
          </b-button>
        </b-col>
      </b-row>
      <b-row>
        <b-col md>
          <b-button v-if="!signed_in" v-b-modal.sign-in-modal class="my-1" block size="lg" variant="primary">
            <b-icon icon="map" />
            RTCR Sample Site Plans
          </b-button>
          <b-button v-else class="my-1" :class="{ 'has-due': !rtcrCorrections && rtcrButtonDue }" block size="lg" variant="primary" to="rtcr">
            <b-icon icon="map" />
            RTCR Sample Site Plans
            <b-badge v-if="rtcrCorrections" variant="warning" class="rtcr-badge">Corrections Required</b-badge>
            <small v-else-if="rtcrButtonDue" class="due-note">{{ rtcrButtonDue }}</small>
          </b-button>
        </b-col>
        <b-col md>
          <b-button v-if="!signed_in" v-b-modal.sign-in-modal class="my-1" block size="lg" variant="primary">
            <b-icon icon="clipboard-check" />
            Monitoring Waivers
          </b-button>
          <b-button v-else class="my-1" block size="lg" variant="primary" to="monitoring-waivers">
            <b-icon icon="clipboard-check" />
            Monitoring Waivers
            <b-badge v-if="waiverDeadlineToday" variant="danger">Due Today</b-badge>
            <b-badge v-if="vocWaiverActionNeeded" variant="info">!</b-badge>
          </b-button>
        </b-col>
      </b-row>
      <b-row v-if="signed_in && fourlogCovered">
        <b-col md>
          <b-button class="my-1" block size="lg" variant="primary" to="fourlog">
            <b-icon icon="shield-check" />
            4-Log Residual Log
          </b-button>
        </b-col>
        <b-col md></b-col>
      </b-row>

      <!-- Lead Testing & Inventory -->
      <h5 class="section-heading mt-4">Lead Testing &amp; Inventory</h5>
      <b-row>
        <b-col md>
          <b-button v-if="!signed_in" v-b-modal.sign-in-modal class="my-1" block size="lg" variant="primary">
            <b-icon icon="droplet" />
            Lead and Copper Sampling and Reporting
          </b-button>
          <b-button v-else class="my-1" block size="lg" variant="primary" to="lead-results">
            <b-icon icon="droplet" />
            Lead and Copper Sampling and Reporting
          </b-button>
        </b-col>
        <b-col md>
          <b-button v-if="!signed_in" v-b-modal.sign-in-modal class="my-1" block size="lg" variant="primary">
            <b-icon icon="card-checklist" />
            Lead Service Line Inventory
          </b-button>
          <b-button v-else class="my-1" block size="lg" variant="primary" to="lead-inventory">
            <b-icon icon="card-checklist" />
            Lead Service Line Inventory
          </b-button>
        </b-col>
        <b-col md>
          <b-button v-if="!signed_in" v-b-modal.sign-in-modal class="my-1" block size="lg" variant="primary">
            <b-icon icon="droplet-half" />
            Water Quality Parameters (WQP)
          </b-button>
          <b-button v-else class="my-1" block size="lg" variant="primary" to="wqp">
            <b-icon icon="droplet-half" />
            Water Quality Parameters (WQP)
          </b-button>
        </b-col>
      </b-row>

      <!-- Compliance & Notices -->
      <h5 class="section-heading mt-4">Compliance &amp; Notices</h5>
      <b-row>
        <b-col md>
          <b-button v-if="!signed_in || !sigdef_status.available" class="my-1" block size="lg" variant="primary" disabled>
            <b-icon icon="clipboard-check" />
            Significant Deficiencies
          </b-button>
          <b-button v-else class="my-1" block size="lg" variant="primary" to="sigdef-responses">
            <b-icon icon="clipboard-check" />
            Significant Deficiencies
            <b-badge v-if="sigdef_status.needs_attention" variant="danger">!</b-badge>
          </b-button>
        </b-col>
        <b-col md>
          <b-button v-if="!signed_in" v-b-modal.sign-in-modal class="my-1" block size="lg" variant="primary">
            <b-icon icon="exclamation-triangle" />
            Emergency Notice
          </b-button>
          <b-button v-else class="my-1" block size="lg" variant="primary" to="emergency">
            <b-icon icon="exclamation-triangle" />
            Emergency Notice
          </b-button>
        </b-col>
      </b-row>

      <!-- System Management -->
      <h5 class="section-heading mt-4">System Management</h5>
      <b-row>
        <b-col md>
          <b-button v-if="!signed_in" v-b-modal.sign-in-modal class="my-1" block size="lg" variant="primary">
            <b-icon icon="people" />
            Board Members
          </b-button>
          <b-button v-else class="my-1" :class="{ 'has-due': !boardMembersCorrections && boardMembersDue }" block size="lg" variant="primary"
            :to="boardMembersCorrections ? { path: '/board-members', query: { tab: 'status' } } : 'board-members'">
            <b-icon icon="people" />
            Board Members
            <b-badge v-if="boardMembersCorrections" variant="warning">Corrections Required</b-badge>
            <small v-else-if="boardMembersDue" class="due-note">{{ boardMembersDue }}</small>
          </b-button>
        </b-col>
        <b-col md>
          <b-button v-if="!signed_in" v-b-modal.sign-in-modal class="my-1" block size="lg" variant="primary">
            <b-icon icon="archive" />
            File Cabinet
          </b-button>
          <b-button v-else class="my-1" block size="lg" variant="primary" to="pws-files">
            <b-icon icon="archive" />
            File Cabinet
          </b-button>
        </b-col>
      </b-row>
      <b-row>
        <b-col md>
          <b-button v-if="!signed_in" v-b-modal.sign-in-modal class="my-1" block size="lg" variant="primary">
            <b-icon icon="inbox-fill" />
            Messages
          </b-button>
          <b-button v-else class="my-1" block size="lg" variant="primary" to="messages">
            <b-icon icon="inbox-fill" />
            Messages
            <b-badge v-if="message_count > 0" variant="light">{{ message_count }}</b-badge>
          </b-button>
        </b-col>
        <b-col md>
          <b-button v-if="!signed_in" v-b-modal.sign-in-modal class="my-1" block size="lg" variant="primary">
            E-Mail Tester
          </b-button>
          <b-button v-else class="my-1" block size="lg" variant="primary" to="email-test">
            E-Mail Tester
          </b-button>
        </b-col>
      </b-row>

    </b-container>

    <b-container fluid class="mt-4 p-0">
      <h3>Additional Applications (External Websites)</h3>
      <p>These applications are available on other websites.</p>
      <b-row>
        <b-col md>
          <b-button class="my-1" block size="lg" variant="outline-primary" href="https://apps.msdh.ms.gov/DWW/">
            Drinking Water Watch
          </b-button>
        </b-col>
        <b-col md>
          <b-button class="my-1" block size="lg" variant="outline-primary" href="https://samples.mswater.us">
            TCR Sample Notification App
          </b-button>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="6">
          <b-button class="my-1" block size="lg" variant="outline-primary" target="_blank"
            href="https://www.msdhwo.webapps.ms.gov/home.aspx">
            LARS (Licensing &amp; Reporting System) LOGIN
          </b-button>
        </b-col>
        <b-col md="6">
          <b-button class="my-1" block size="lg" variant="outline-primary" target="_blank"
            href="https://www.msdhwo.webapps.ms.gov/PublicView/PublicIndSearch.aspx">
            Operator Search (Find a Certified Operator)
          </b-button>
        </b-col>
      </b-row>

    </b-container>
    <div class="mt-5 text-center">
      <b-link to="help">Need Help?</b-link>
    </div>

    <b-modal id="sign-in-modal" title="Sign In Required" @ok="$emit('nav-to-signin')">
      <p>You need to sign in before accessing this application.</p>
    </b-modal>

    <!-- Bacteriological sampling modal -->
    <b-modal v-model="showBactFormModal" title="Important messages regarding bacteriological sampling" size="lg" ok-only ok-title="Close">
      <p>
        The <strong>Form 425</strong> was revised. Copies should be available throughout county health departments.
      </p>
      <p>
        For <strong>Bact bar code labels</strong>, click on instructions below.
      </p>
      <p class="text-center">
        <b-button variant="primary" href="/static/docs/Bact_bar_code_labels.pdf" target="_blank">
          <b-icon icon="download"></b-icon> Download (PDF)
        </b-button>
      </p>
      <p class="text-muted">May 2026</p>
    </b-modal>
  </div>
</template>

<script>
import { ANNOUNCEMENTS, isShown, dismiss } from "@/announcements"

export default {
  name: "HomeClassic",
  props: {
    signed_in: { type: Boolean, default: false },
    pws_id: { type: String, default: '' },
    role: { type: String, default: '' },
    message_count: { type: Number, default: 0 },
    lsli_notice_due: { type: Boolean, default: false },
    vocWaiverActionNeeded: { type: Boolean, default: false },
    lsli_notice_year: { type: [Number, String], default: null },
    sigdef_status: {
      type: Object,
      default: () => ({
        available: false,
        unresolved_count: 0,
        pending_response_count: 0,
        needs_attention: false,
      })
    },
    waiverDeadlineToday: { type: Boolean, default: false },
    annualReportDue: { type: String, default: '' },
    boardMembersDue: { type: String, default: '' },
    boardMembersCorrections: { type: Boolean, default: false },
    rtcrCorrections: { type: Boolean, default: false },
    rtcrPlanDue: { type: String, default: '' },
    rtcrButtonDue: { type: String, default: '' },
    fourlogCovered: { type: Boolean, default: false },
    showSigninAlert: { type: Boolean, default: false },
    showAuthError: { type: Boolean, default: false },
    authErrorMessage: { type: String, default: '' }
  },
  data() {
    return {
      showBactFormModal: false,
      bactShown: isShown(ANNOUNCEMENTS.bact_form_425),
      localShowSigninAlert: this.showSigninAlert,
      localShowAuthError: this.showAuthError,
      local_auth_error_message: this.authErrorMessage
    }
  },
  watch: {
    showSigninAlert(val) {
      this.localShowSigninAlert = val
    },
    showAuthError(val) {
      this.localShowAuthError = val
    },
    authErrorMessage(val) {
      this.local_auth_error_message = val
    }
  },
  methods: {
    dismissBact() {
      dismiss(ANNOUNCEMENTS.bact_form_425)
      this.bactShown = false
    },
  },
}
</script>

<style scoped>
/* REQ-156: a due date at the right edge of a menu button; on narrower
   screens it sits under the label so the two never overlap. */
.due-note {
  display: block;
  font-size: 0.75rem;
  opacity: 0.9;
}
@media (min-width: 992px) {
  .btn-block { position: relative; }
  /* room for the note, so the centered label never runs into it */
  .btn-primary.has-due { padding-right: 9.5rem !important; }
  .due-note {
    position: absolute;
    right: 0.9rem;
    top: 50%;
    transform: translateY(-50%);
  }
}
/* Add pronounced shadow to primary buttons */
.btn-primary {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25) !important;
  transition: all 0.2s ease;
  border-radius: 8px !important;
  padding: 12px 20px !important;
}

/* Hover effects */
.btn-primary:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3) !important;
}

/* Style outline-primary buttons (Additional Applications) */
.btn-outline-primary {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25) !important;
  transition: all 0.2s ease;
  border-radius: 8px !important;
  padding: 12px 20px !important;
}


.btn-outline-primary:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.3) !important;
}

/* Style headings with decorative line */
h3 {
  position: relative;
  padding-bottom: 10px;
  margin-bottom: 25px !important;
}

h3::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(to right, #007bff, rgba(0, 123, 255, 0.1));
}

/* Section headings - smaller, lighter styling */
.section-heading {
  color: #555;
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 15px;
  padding-bottom: 8px;
  border-bottom: 1px solid #dee2e6;
}

/* Attention section styling */
.btn-danger.btn-lg,
.btn-warning.btn-lg {
  font-weight: 600;
}

/* Announcement card styling */
.announcement-card {
  border-width: 2px !important;
  box-shadow: 0 2px 8px rgba(23, 162, 184, 0.2);
}

.announcement-card h5 {
  font-weight: 600;
}

.announcement-dismiss {
  color: #6c757d;
}

.clickable {
  cursor: pointer;
}
</style>
